import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  Search, 
  BookOpen, 
  ArrowLeft,
  Tag,
  FileText,
  HelpCircle,
  Loader2
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const KnowledgePortal = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/knowledge/articles`),
        axios.get(`${API}/knowledge/categories`)
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error fetching knowledge data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const faqs = [
    {
      question: "What documents do I need to apply?",
      answer: "For most loan applications, you'll need basic business information, financial statements (revenue, expenses), and details about your business operations. We also accept alternative data like bank statements, social media metrics, and payment history to strengthen your application."
    },
    {
      question: "How long does the approval process take?",
      answer: "Our AI-powered risk assessment enables us to provide initial decisions within 24-48 hours for most applications. Complex cases may require additional review time."
    },
    {
      question: "What is alternative data and why do you use it?",
      answer: "Alternative data includes non-traditional financial information like social media presence, online reviews, cash flow patterns, and vendor payment history. We use this data to get a more complete picture of your business, which can help businesses with limited credit history access financing."
    },
    {
      question: "What interest rates can I expect?",
      answer: "Interest rates vary based on loan type, amount, term, and your business's risk profile. Our rates typically range from 7% to 18% APR, with better rates available for lower-risk applications."
    },
    {
      question: "Can I apply if I have a new business?",
      answer: "Yes! While traditional lenders often require years of history, we use alternative data to assess newer businesses. If your business shows strong fundamentals and growth potential, you may still qualify."
    },
    {
      question: "How do I track my application status?",
      answer: "After logging into your account, you can view all your applications and their current status on your dashboard. You'll also receive email notifications when your application status changes."
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="knowledge-portal">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Tzur Finances" className="h-10" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Loan Products
              </Link>
              <Link to="/knowledge" className="text-[#0A2540] font-medium">
                Knowledge Portal
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A2540]">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Knowledge Portal
          </h1>
          <p className="text-gray-300 text-lg mb-8">
            Everything you need to know about small business financing
          </p>
          
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search articles, topics, or questions..."
              className="pl-12 h-12 bg-white border-0 shadow-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(null)}
            className={selectedCategory === null ? "bg-[#0A2540]" : ""}
            data-testid="filter-all"
          >
            All Topics
          </Button>
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? "bg-[#0A2540]" : ""}
              data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
            >
              {category}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Articles */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Articles & Guides
            </h2>
            
            {filteredArticles.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No articles found</h3>
                  <p className="text-gray-500 mt-1">Try adjusting your search or filter</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredArticles.map((article, index) => (
                  <Card 
                    key={article.article_id || index} 
                    className="bg-white hover:border-[#0A2540]/20 transition-colors"
                    data-testid={`article-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Badge variant="secondary" className="mb-2">
                            {article.category}
                          </Badge>
                          <h3 className="text-lg font-semibold text-[#0A2540] mb-2">
                            {article.title}
                          </h3>
                          <p className="text-gray-600">{article.content}</p>
                          
                          {article.tags && article.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4">
                              {article.tags.map((tag, tagIndex) => (
                                <span 
                                  key={tagIndex}
                                  className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded"
                                >
                                  <Tag className="h-3 w-3" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* FAQs Sidebar */}
          <div>
            <h2 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2 mb-6">
              <HelpCircle className="h-6 w-6" />
              FAQs
            </h2>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left text-sm font-medium text-[#0A2540]">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="bg-[#0A2540] text-white mt-6">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2">Ready to Apply?</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Get the funding your business needs
                </p>
                <Link to="/register">
                  <Button className="bg-amber-500 text-[#0A2540] hover:bg-amber-400 w-full" data-testid="cta-apply-btn">
                    Start Application
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          <p>© 2024 Tzur Finances. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default KnowledgePortal;
